import { Helmet } from 'react-helmet';

const HowReduced18PercentWaste = () => (
    <section className="section">
        <Helmet>
            <title>How a Paper Mill Reduced 18% Waste Using DecklePro | DecklePro Blog</title>
            <meta
                name="description"
                content="Discover how a kraft paper mill reduced 18% trim waste and boosted production efficiency using DecklePro - the smart deckle matching software by Anmatics."
            />
            <meta
                name="keywords"
                content="DecklePro, paper mill software, deckle matching, kraft paper mill, waste reduction, production optimization, Anmatics"
            />
            <link
                rel="canonical"
                href="https://www.anmatics.com/blogs/how-reduced-18-percent-waste"
            />
            <meta
                property="og:title"
                content="How a Paper Mill Reduced 18% Waste Using DecklePro"
            />
            <meta
                property="og:description"
                content="See how DecklePro helped a kraft paper mill cut 18% trim waste and streamline production efficiency with smart deckle matching technology."
            />
            <meta property="og:type" content="article" />
            <meta
                property="og:url"
                content="https://www.anmatics.com/blogs/how-reduced-18-percent-waste"
            />
            <meta
                property="og:image"
                content="https://www.anmatics.com/assets/images/decklepro-waste-reduction.jpg"
            />
        </Helmet>

        <div className="container" style={{ maxWidth: '820px' }}>
            <header
                className="mb-2 pb-3 border-bottom"
                style={{ background: 'none', boxShadow: 'none' }}
            >
                <h1
                    className="fw-bold mb-3"
                    style={{ color: 'var(--anmatics-primary)' }}
                    itemProp="headline"
                >
                    How a Paper Mill Reduced 18% Waste Using DecklePro
                </h1>
                <p className="text-secondary">
                    <strong>Published By Anmatics Technologies</strong>
                </p>
            </header>

            <article itemScope itemType="https://schema.org/Article">
                <meta itemProp="author" content="DecklePro Team" />
                <meta
                    itemProp="description"
                    content="Discover how a kraft paper mill reduced 18% trim waste and improved production efficiency using DecklePro - the smart deckle matching software by Anmatics."
                />
                <meta
                    itemProp="image"
                    content="https://www.anmatics.com/assets/images/decklepro-waste-reduction.jpg"
                />
                <meta
                    itemProp="url"
                    content="https://www.anmatics.com/blogs/how-reduced-18-percent-waste"
                />

                <p itemProp="articleBody">
                    In the kraft paper industry, even small waste reductions mean big savings.
                    One of our clients, a mid-sized paper mill, achieved an
                    <strong> 18% reduction in trim waste</strong> after implementing
                    <strong> DecklePro</strong>, Anmatics’ smart deckle matching software.
                </p>

                <h2
                    className="mt-4"
                    style={{ color: 'var(--anmatics-primary-hover)' }}
                    id="the-challenge"
                >
                    The Challenge
                </h2>
                <p>
                    The mill used Excel sheets for deckle matching, leading to manual errors,
                    high trim waste, and planning delays. They needed a faster, more accurate
                    system to automate matching and improve efficiency.
                </p>

                <h2
                    className="mt-4"
                    style={{ color: 'var(--anmatics-primary-hover)' }}
                    id="the-solution"
                >
                    The Solution: DecklePro
                </h2>
                <p>
                    <strong>DecklePro</strong> simplified the entire process by:
                </p>
                <ul>
                    <li>Automating deckle matching across BF, GSM, diameter, and shade</li>
                    <li>Optimizing reel cutting to minimize waste</li>
                    <li>Providing real-time dashboards for planning and inventory</li>
                </ul>
                <p>
                    Within weeks, production teams could generate accurate plans in minutes
                    instead of hours.
                </p>

                <h2
                    className="mt-4"
                    style={{ color: 'var(--anmatics-primary-hover)' }}
                    id="results"
                >
                    The Results
                </h2>

                <table className="table table-bordered mt-3">
                    <thead>
                        <tr style={{ background: 'var(--anmatics-primary)', color: '#fff' }}>
                            <th>Metric</th>
                            <th>Before</th>
                            <th>After</th>
                            <th>Improvement</th>
                        </tr>
                    </thead>
                    <tbody>
                        <tr>
                            <td>Trim Waste</td>
                            <td>12%</td>
                            <td>9.8%</td>
                            <td>🔻 18% less</td>
                        </tr>
                        <tr>
                            <td>Planning Time</td>
                            <td>2–3 hrs</td>
                            <td>15 min</td>
                            <td>⚡ 90% faster</td>
                        </tr>
                        <tr>
                            <td>Order Accuracy</td>
                            <td>82%</td>
                            <td>97%</td>
                            <td>🎯 Improved</td>
                        </tr>
                    </tbody>
                </table>

                <h2
                    className="mt-4"
                    style={{ color: 'var(--anmatics-primary-hover)' }}
                    id="takeaways"
                >
                    Key Takeaways
                </h2>
                <ul>
                    <li>Automation reduced manual effort and waste</li>
                    <li>Production planning became faster and more reliable</li>
                    <li>Real-time visibility improved decision-making</li>
                </ul>

                <h3
                    className="mt-4"
                    style={{ color: 'var(--anmatics-primary-hover)' }}
                    id="about-decklepro"
                >
                    About DecklePro
                </h3>
                <p>
                    <strong>DecklePro</strong> by <strong>Anmatics</strong> helps kraft paper
                    mills optimize production, reduce trim waste, and simplify operations -
                    all in one smart platform.
                </p>

                <blockquote
                    className="p-3 mt-3"
                    style={{
                        background: '#f8f9fa',
                        borderLeft: '4px solid var(--anmatics-primary)',
                        fontStyle: 'italic',
                    }}
                >
                    “DecklePro transformed our workflow - less waste, faster planning, and
                    better output.”
                    <footer className="blockquote-footer mt-2" style={{ color: '#fff' }}>
                        Production Head, Kraft Paper Mill
                    </footer>
                </blockquote>

                <div
                    className="alert alert-primary mt-4"
                    style={{ background: 'var(--anmatics-primary)', color: '#fff' }}
                >
                    <strong>Want to see how DecklePro can optimize your operations?</strong>{' '}
                    <a
                        href="/contact"
                        style={{ color: '#fff', textDecoration: 'underline' }}
                    >
                        Book a free demo today!
                    </a>
                </div>
            </article>
        </div>

    </section>
);

export default HowReduced18PercentWaste;